import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings } from '../../services/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'react-toastify';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { uploadImage } from '../../services/imageService';

const BannerManagementPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [mobileImageFile, setMobileImageFile] = useState(null);
  const [mobileImagePreview, setMobileImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    type: 'image',
    url: '',
    mobileUrl: '',
    order: 0,
    isActive: true,
  });
  const [shopNowLink, setShopNowLink] = useState('/shop');
  const [autoSlideInterval, setAutoSlideInterval] = useState(5000);
  const [isPromoDialogOpen, setIsPromoDialogOpen] = useState(false);
  const [editingPromoIndex, setEditingPromoIndex] = useState(null);
  const [promoFormData, setPromoFormData] = useState({
    desktopUrl: '',
    mobileUrl: '',
    displayDuration: 5,
    order: 0,
    isActive: true,
  });
  const [promoDesktopFile, setPromoDesktopFile] = useState(null);
  const [promoDesktopPreview, setPromoDesktopPreview] = useState(null);
  const [promoMobileFile, setPromoMobileFile] = useState(null);
  const [promoMobilePreview, setPromoMobilePreview] = useState(null);
  const [uploadingPromo, setUploadingPromo] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      // console.log('Fetched settings:', response);
      if (response.success) {
        // Ensure homeBanner structure exists
        const settings = response.settings;
        if (!settings.homeBanner) {
          settings.homeBanner = {
            items: [],
            shopNowLink: '/shop',
            autoSlideInterval: 5000,
            seasonalPromoImages: []
          };
        }
        if (!settings.homeBanner.seasonalPromoImages) {
          settings.homeBanner.seasonalPromoImages = [];
        }

        setSettings(settings);
        const banner = settings.homeBanner || {};
        setShopNowLink(banner.shopNowLink || '/shop');
        setAutoSlideInterval(banner.autoSlideInterval || 5000);
        // console.log('Promo images loaded:', banner.seasonalPromoImages);
      }
    } catch (error) {
      toast.error('Failed to fetch banner settings');
      // console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setSelectedImageFile(null);
    setImagePreview(null);
    setMobileImageFile(null);
    setMobileImagePreview(null);
    setFormData({
      type: 'image',
      url: '',
      mobileUrl: '',
      order: (settings?.homeBanner?.items?.length || 0),
      isActive: true,
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setSelectedImageFile(null);
    setImagePreview(null);
    setMobileImageFile(null);
    setMobileImagePreview(null);
    setFormData({
      type: item.type,
      url: item.url,
      mobileUrl: item.mobileUrl || '',
      order: item.order,
      isActive: item.isActive,
    });
    setIsDialogOpen(true);
  };

  const handleImageFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMobileImageFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setMobileImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setMobileImagePreview(event.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async (isMobile = false) => {
    const file = isMobile ? mobileImageFile : selectedImageFile;
    if (!file) {
      toast.error('Please select an image');
      return;
    }

    try {
      setUploadingImage(true);
      const response = await uploadImage(file);

      if (response.success && response.data?.url) {
        const imageUrl = response.data.url;
        if (isMobile) {
          setFormData({ ...formData, mobileUrl: imageUrl });
          setMobileImageFile(null);
          toast.success('Mobile image uploaded successfully');
        } else {
          setFormData({ ...formData, url: imageUrl });
          setSelectedImageFile(null);
          toast.success('Desktop image uploaded successfully');
        }
      } else {
        toast.error(response.error || 'Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading image');
      // console.error(error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddPromoImage = () => {
    setEditingPromoIndex(null);
    setPromoDesktopFile(null);
    setPromoDesktopPreview(null);
    setPromoMobileFile(null);
    setPromoMobilePreview(null);
    setPromoFormData({
      desktopUrl: '',
      mobileUrl: '',
      displayDuration: 5,
      order: (settings?.homeBanner?.seasonalPromoImages?.length || 0),
      isActive: true,
    });
    setIsPromoDialogOpen(true);
  };

  const handleEditPromoImage = (index, image) => {
    setEditingPromoIndex(index);
    setPromoDesktopFile(null);
    setPromoDesktopPreview(image.desktopUrl);
    setPromoMobileFile(null);
    setPromoMobilePreview(image.mobileUrl);
    setPromoFormData({
      desktopUrl: image.desktopUrl,
      mobileUrl: image.mobileUrl || '',
      displayDuration: image.displayDuration || 5,
      order: image.order,
      isActive: image.isActive,
    });
    setIsPromoDialogOpen(true);
  };

  const handlePromoFileSelect = (e, isMobile = false) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (isMobile) {
          setPromoMobileFile(file);
          setPromoMobilePreview(event.target?.result);
        } else {
          setPromoDesktopFile(file);
          setPromoDesktopPreview(event.target?.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPromoImage = async (isMobile = false) => {
    const file = isMobile ? promoMobileFile : promoDesktopFile;
    if (!file) {
      toast.error('Please select an image');
      return;
    }

    try {
      setUploadingPromo(true);
      const response = await uploadImage(file);

      if (response.success && response.data?.url) {
        const imageUrl = response.data.url;
        if (isMobile) {
          setPromoFormData({ ...promoFormData, mobileUrl: imageUrl });
          setPromoMobileFile(null);
          toast.success('Mobile image uploaded successfully');
        } else {
          setPromoFormData({ ...promoFormData, desktopUrl: imageUrl });
          setPromoDesktopFile(null);
          toast.success('Desktop image uploaded successfully');
        }
      } else {
        toast.error(response.error || 'Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading image');
      // console.error(error);
    } finally {
      setUploadingPromo(false);
    }
  };

  const handleSavePromoImage = async () => {
    if (!promoFormData.desktopUrl) {
      toast.error('Desktop image is required');
      return;
    }

    try {
      // Create a copy of settings to avoid mutation
      let settingsCopy = JSON.parse(JSON.stringify(settings));

      // Ensure homeBanner exists
      if (!settingsCopy.homeBanner) {
        settingsCopy.homeBanner = {
          items: [],
          shopNowLink: '/shop',
          autoSlideInterval: 5000,
          seasonalPromoImages: []
        };
      }

      const promoImages = [...(settingsCopy.homeBanner.seasonalPromoImages || [])];

      if (editingPromoIndex !== null) {
        // Update existing
        promoImages[editingPromoIndex] = promoFormData;
      } else {
        // Add new
        promoImages.push(promoFormData);
      }

      const updatedSettings = {
        ...settingsCopy,
        homeBanner: {
          ...settingsCopy.homeBanner,
          seasonalPromoImages: promoImages,
        },
      };

      // console.log('Saving promo image with data:', updatedSettings.homeBanner.seasonalPromoImages);
      // console.log('Full updatedSettings:', JSON.stringify(updatedSettings, null, 2));
      const response = await updateSettings(updatedSettings);
      // console.log('Response from updateSettings:', response);

      if (response.success) {
        // console.log('Settings after save:', response.settings);
        // Ensure response.settings has proper structure
        let newSettings = response.settings;
        if (!newSettings.homeBanner) {
          newSettings = {
            ...newSettings,
            homeBanner: {
              items: [],
              shopNowLink: '/shop',
              autoSlideInterval: 5000,
              seasonalPromoImages: []
            }
          };
        }
        setSettings(newSettings);
        setIsPromoDialogOpen(false);
        // Reset form
        setPromoFormData({
          desktopUrl: '',
          mobileUrl: '',
          displayDuration: 5,
          order: 0,
          isActive: true,
        });
        setEditingPromoIndex(null);
        toast.success(editingPromoIndex !== null ? 'Promo image updated' : 'Promo image added');
      } else {
        console.error('Save failed:', response);
        toast.error(response.error || 'Failed to save promo image');
      }
    } catch (error) {
      toast.error('Failed to save promo image');
      console.error('Save error:', error);
    }
  };

  const handleDeletePromoImage = async (index) => {
    if (!window.confirm('Are you sure you want to delete this promo image?')) {
      return;
    }

    try {
      const promoImages = settings?.homeBanner?.seasonalPromoImages?.filter((_, i) => i !== index) || [];
      const updatedSettings = {
        ...settings,
        homeBanner: {
          ...settings.homeBanner,
          seasonalPromoImages: promoImages,
        },
      };

      const response = await updateSettings(updatedSettings);
      if (response.success) {
        setSettings(response.settings);
        toast.success('Promo image deleted');
      }
    } catch (error) {
      toast.error('Failed to delete promo image');
      // console.error(error);
    }
  };

  const handleTogglePromoActive = async (index) => {
    const images = [...(settings?.homeBanner?.seasonalPromoImages || [])];
    images[index].isActive = !images[index].isActive;

    try {
      const updatedSettings = {
        ...settings,
        homeBanner: {
          ...settings.homeBanner,
          seasonalPromoImages: images,
        },
      };

      const response = await updateSettings(updatedSettings);
      if (response.success) {
        setSettings(response.settings);
        toast.success(`Promo image ${images[index].isActive ? 'activated' : 'deactivated'}`);
      }
    } catch (error) {
      toast.error('Failed to update promo image status');
      // console.error(error);
    }
  };

  const handleReorderPromo = async (index, direction) => {
    const images = [...(settings?.homeBanner?.seasonalPromoImages || [])];
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= images.length) return;

    [images[index], images[newIndex]] = [images[newIndex], images[index]];
    images[index].order = index;
    images[newIndex].order = newIndex;

    try {
      const updatedSettings = {
        ...settings,
        homeBanner: {
          ...settings.homeBanner,
          seasonalPromoImages: images,
        },
      };

      const response = await updateSettings(updatedSettings);
      if (response.success) {
        setSettings(response.settings);
        toast.success('Promo image reordered');
      }
    } catch (error) {
      toast.error('Failed to reorder promo image');
      // console.error(error);
    }
  };

  const handleSaveItem = async () => {
    if (!formData.url) {
      toast.error('URL is required');
      return;
    }

    try {
      const bannerItems = [...(settings?.homeBanner?.items || [])];

      if (editingItem) {
        // Update existing item
        const index = bannerItems.findIndex(item => item._id === editingItem._id);
        if (index !== -1) {
          bannerItems[index] = { ...bannerItems[index], ...formData };
        }
      } else {
        // Add new item
        bannerItems.push(formData);
      }

      const updatedSettings = {
        ...settings,
        homeBanner: {
          items: bannerItems,
          shopNowLink,
          autoSlideInterval,
        },
      };

      const response = await updateSettings(updatedSettings);
      if (response.success) {
        setSettings(response.settings);
        setIsDialogOpen(false);
        toast.success(editingItem ? 'Banner item updated' : 'Banner item added');
      }
    } catch (error) {
      toast.error('Failed to save banner item');
      // console.error(error);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this banner item?')) {
      return;
    }

    try {
      const bannerItems = settings.homeBanner.items.filter(item => item._id !== itemId);
      const updatedSettings = {
        ...settings,
        homeBanner: {
          ...settings.homeBanner,
          items: bannerItems,
        },
      };

      const response = await updateSettings(updatedSettings);
      if (response.success) {
        setSettings(response.settings);
        toast.success('Banner item deleted');
      }
    } catch (error) {
      toast.error('Failed to delete banner item');
      // console.error(error);
    }
  };

  const handleSaveBannerSettings = async () => {
    try {
      const updatedSettings = {
        ...settings,
        homeBanner: {
          items: settings.homeBanner.items,
          shopNowLink,
          autoSlideInterval,
        },
      };

      const response = await updateSettings(updatedSettings);
      if (response.success) {
        setSettings(response.settings);
        toast.success('Banner settings saved');
      }
    } catch (error) {
      toast.error('Failed to save banner settings');
      // console.error(error);
    }
  };

  const handleReorder = async (itemId, direction) => {
    const items = [...(settings.homeBanner.items || [])];
    const index = items.findIndex(item => item._id === itemId);

    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    items[index].order = index;
    items[newIndex].order = newIndex;

    try {
      const updatedSettings = {
        ...settings,
        homeBanner: {
          ...settings.homeBanner,
          items,
        },
      };

      const response = await updateSettings(updatedSettings);
      if (response.success) {
        setSettings(response.settings);
        toast.success('Banner item reordered');
      }
    } catch (error) {
      toast.error('Failed to reorder banner item');
      // console.error(error);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  const bannerItems = settings?.homeBanner?.items || [];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Banner Management</h1>
        <Button onClick={handleAddNew}>Add New Banner Item</Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Banner Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="shopNowLink">Shop Now Button Link</Label>
              <Input
                id="shopNowLink"
                value={shopNowLink}
                onChange={(e) => setShopNowLink(e.target.value)}
                placeholder="/shop"
              />
            </div>
            <div>
              <Label htmlFor="autoSlideInterval">Auto Slide Interval (milliseconds)</Label>
              <Input
                id="autoSlideInterval"
                type="number"
                value={autoSlideInterval}
                onChange={(e) => setAutoSlideInterval(parseInt(e.target.value) || 5000)}
                min="1000"
                step="1000"
              />
              <p className="text-sm text-gray-500 mt-1">
                How long each image displays before auto-advancing (only applies to image slideshows)
              </p>
            </div>
            <Button onClick={handleSaveBannerSettings}>Save Settings</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Promo Images (Shows on Category & Shop Pages)</CardTitle>
            <Button onClick={handleAddPromoImage}>Add Promo Image</Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* {console.log('Current promo images:', settings?.homeBanner?.seasonalPromoImages)} */}
          {!settings?.homeBanner?.seasonalPromoImages || settings.homeBanner.seasonalPromoImages.length === 0 ? (
            <p className="text-gray-500">No promo images configured. Add one to get started.</p>
          ) : (
            <div className="space-y-4">
              {(settings.homeBanner.seasonalPromoImages || [])
                .sort((a, b) => a.order - b.order)
                .map((image, index) => (
                  <div key={index} className="border rounded p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">Promo {index + 1}</span>
                        <span className={`px-2 py-1 rounded text-xs ${image.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {image.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-sm text-gray-500">Duration: {image.displayDuration}s</span>
                        <span className="text-sm text-gray-500">Order: {image.order}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">Desktop: {image.desktopUrl}</p>
                      {image.mobileUrl && (
                        <p className="text-xs text-gray-500 truncate">Mobile: {image.mobileUrl}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReorderPromo(index, 'up')}
                        disabled={index === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReorderPromo(index, 'down')}
                        disabled={index === (settings.homeBanner.seasonalPromoImages?.length || 0) - 1}
                      >
                        ↓
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTogglePromoActive(index)}
                        className={image.isActive ? 'text-green-600 hover:text-green-700' : 'text-orange-600 hover:text-orange-700'}
                      >
                        {image.isActive ? 'Hide' : 'Show'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditPromoImage(index, image)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeletePromoImage(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Banner Items</CardTitle>
        </CardHeader>
        <CardContent>
          {bannerItems.length === 0 ? (
            <p className="text-gray-500">No banner items configured. Add one to get started.</p>
          ) : (
            <div className="space-y-4">
              {bannerItems
                .sort((a, b) => a.order - b.order)
                .map((item, index) => (
                  <div key={item._id} className="border rounded p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">{item.type === 'video' ? 'Video' : 'Image'}</span>
                        <span className={`px-2 py-1 rounded text-xs ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-sm text-gray-500">Order: {item.order}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{item.url}</p>
                      {item.mobileUrl && (
                        <p className="text-xs text-gray-500 truncate">Mobile: {item.mobileUrl}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReorder(item._id, 'up')}
                        disabled={index === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReorder(item._id, 'down')}
                        disabled={index === bannerItems.length - 1}
                      >
                        ↓
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(item._id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Banner Item' : 'Add Banner Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto max-h-[calc(90vh-180px)]">
            <div>
              <Label htmlFor="type">Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">Image (PNG, JPEG, JPG, WEBP)</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.type === 'image' && (
              <>
                <div>
                  <Label htmlFor="desktopImage" className="mb-2 block">Upload Desktop Image</Label>
                  <div className="flex gap-2">
                    <input
                      id="desktopImage"
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileSelect}
                      className="flex-1 block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded file:border-0
                        file:text-sm file:font-medium
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                    <button
                      type="button"
                      onClick={() => handleUploadImage(false)}
                      disabled={!selectedImageFile || uploadingImage}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {uploadingImage ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                  {imagePreview && (
                    <div className="mt-2">
                      <img src={imagePreview} alt="Desktop preview" className="w-full max-h-48 object-cover rounded" />
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="url">Desktop Image URL</Label>
                  <Input
                    id="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://... or R2 URL will appear here after upload"
                    readOnly={selectedImageFile !== null}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Upload using the file input above or paste a direct URL
                  </p>
                </div>

                <div>
                  <Label htmlFor="mobileImage" className="mb-2 block">Upload Mobile Image (Optional)</Label>
                  <div className="flex gap-2">
                    <input
                      id="mobileImage"
                      type="file"
                      accept="image/*"
                      onChange={handleMobileImageFileSelect}
                      className="flex-1 block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded file:border-0
                        file:text-sm file:font-medium
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                    <button
                      type="button"
                      onClick={() => handleUploadImage(true)}
                      disabled={!mobileImageFile || uploadingImage}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {uploadingImage ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                  {mobileImagePreview && (
                    <div className="mt-2">
                      <img src={mobileImagePreview} alt="Mobile preview" className="w-full max-h-48 object-cover rounded" />
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="mobileUrl">Mobile Image URL (Optional)</Label>
                  <Input
                    id="mobileUrl"
                    value={formData.mobileUrl}
                    onChange={(e) => setFormData({ ...formData, mobileUrl: e.target.value })}
                    placeholder="https://... or R2 URL will appear here after upload"
                    readOnly={mobileImageFile !== null}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    If provided, this will be used on mobile devices instead of the desktop image
                  </p>
                </div>
              </>
            )}

            {formData.type === 'video' && (
              <>
                <div>
                  <Label htmlFor="url">URL (Desktop)</Label>
                  <Input
                    id="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://example.com/video.mp4"
                  />
                </div>
                <div>
                  <Label htmlFor="mobileUrl">Mobile URL (Optional)</Label>
                  <Input
                    id="mobileUrl"
                    value={formData.mobileUrl}
                    onChange={(e) => setFormData({ ...formData, mobileUrl: e.target.value })}
                    placeholder="https://example.com/mobile-video.mp4"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    If provided, this will be used on mobile devices instead of the desktop URL
                  </p>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                min="0"
              />
              <p className="text-sm text-gray-500 mt-1">
                Lower numbers appear first. Items are sorted by this value.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="isActive" className="cursor-pointer">Active (visible on homepage)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveItem}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isPromoDialogOpen} onOpenChange={setIsPromoDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingPromoIndex !== null ? 'Edit' : 'Add'} Promo Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div>
              <Label className="mb-2 block">Desktop Image</Label>
              <div className="flex gap-2 mb-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePromoFileSelect(e, false)}
                  className="flex-1 block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
                <button
                  type="button"
                  onClick={() => handleUploadPromoImage(false)}
                  disabled={!promoDesktopFile || uploadingPromo}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {uploadingPromo ? 'Uploading...' : 'Upload'}
                </button>
              </div>
              {promoDesktopPreview && (
                <div className="mt-2">
                  <img src={promoDesktopPreview} alt="Desktop preview" className="w-full max-h-48 object-cover rounded" />
                </div>
              )}
            </div>

            <div>
              <Label className="mb-2 block">Mobile Image (Optional)</Label>
              <div className="flex gap-2 mb-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePromoFileSelect(e, true)}
                  className="flex-1 block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
                <button
                  type="button"
                  onClick={() => handleUploadPromoImage(true)}
                  disabled={!promoMobileFile || uploadingPromo}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {uploadingPromo ? 'Uploading...' : 'Upload'}
                </button>
              </div>
              {promoMobilePreview && (
                <div className="mt-2">
                  <img src={promoMobilePreview} alt="Mobile preview" className="w-full max-h-48 object-cover rounded" />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="displayDuration">Display Duration (seconds)</Label>
              <Input
                id="displayDuration"
                type="number"
                value={promoFormData.displayDuration}
                onChange={(e) => setPromoFormData({ ...promoFormData, displayDuration: parseInt(e.target.value) || 5 })}
                min="1"
                step="1"
              />
              <p className="text-sm text-gray-500 mt-1">
                How long this image displays before rotating to the next one (when multiple images exist)
              </p>
            </div>

            <div>
              <Label htmlFor="promoOrder">Display Order</Label>
              <Input
                id="promoOrder"
                type="number"
                value={promoFormData.order}
                onChange={(e) => setPromoFormData({ ...promoFormData, order: parseInt(e.target.value) || 0 })}
                min="0"
              />
              <p className="text-sm text-gray-500 mt-1">
                Lower numbers appear first
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="promoIsActive"
                checked={promoFormData.isActive}
                onChange={(e) => setPromoFormData({ ...promoFormData, isActive: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="promoIsActive" className="cursor-pointer">Active (visible on shop pages)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPromoDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePromoImage}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannerManagementPage;

